import { memo, ReactNode } from 'react'

import { ChevronRight } from '@mui/icons-material'
import { alpha, Skeleton, Stack, Typography } from '@mui/material'
import { AvcTaskHealthEnum, AvcTaskStateEnum } from '@nativewaves/nw-cloud-sdk/content'
import { upperFirst } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { AvcTaskAssociationRow } from 'domains/Workspaces/Streams/AvcTaskAssociations'
import { getAvcTaskStateColor } from 'utils'

type StatePillProps = {
  state?: AvcTaskStateEnum
  health?: AvcTaskHealthEnum
}

const StatePill = ({ state, health }: StatePillProps) => {
  const { t } = useTranslation(['common'])

  if (!state) {
    return (
      <Typography sx={{ width: 120, textAlign: 'center', color: 'text.disabled' }} variant="text-sm" noWrap>
        {t('nA.long')}
      </Typography>
    )
  }

  return (
    <Typography
      sx={{
        width: 120,
        textAlign: 'center',
        display: 'inline-block',
        px: 0.5,
        py: 0.3,
        backgroundColor: (theme) => getAvcTaskStateColor(state, theme.palette.mode, health),
        color: (theme) => theme.palette.getContrastText(getAvcTaskStateColor(state, theme.palette.mode, health)),
        borderRadius: 1,
      }}
      variant="text-sm"
    >
      {upperFirst(state.toLowerCase())}
    </Typography>
  )
}

export type AssociationRowProps = {
  row: AvcTaskAssociationRow
  /** The enabled affordance — a static Chip in read mode, a Switch in edit mode. */
  EnabledSlot: ReactNode
  /** Trailing actions, e.g. unlink. Edit mode only. */
  ActionsSlot?: ReactNode
  /**
   * Signals that the row navigates to the task: pointer cursor, hover feedback and a chevron.
   * The row itself is not the link — a wrapper supplies that — so this only carries the affordance.
   */
  interactive?: boolean
}

const AssociationRowRaw = ({ row, EnabledSlot, ActionsSlot, interactive }: AssociationRowProps) => {
  const { t } = useTranslation(['common'])

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={[
        {
          px: 2,
          py: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.levels.300',
        },
        !!interactive && {
          cursor: 'pointer',
          transition: (theme) => theme.transitions.create(['background-color', 'border-color']),
          '&:hover': {
            // A primary tint rather than another `levels` step: in dark mode the next step down
            // is darker than the Paper behind it, so a hovered card would recede instead of lift.
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderColor: 'primary.main',
          },
        },
      ]}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        {row.isTaskLoading ? (
          <Skeleton variant="text" width="40%" />
        ) : (
          <Typography variant="heading-sm" noWrap>
            {row.task?.name || `${t('id')} (${row.taskId})`}
          </Typography>
        )}
        <Typography variant="text-sm" sx={{ color: 'text.secondary' }} noWrap>
          {row.taskId}
        </Typography>
      </Stack>

      <StatePill state={row.task?.state} health={row.task?.health} />

      {EnabledSlot}
      {ActionsSlot}
      {interactive && <ChevronRight sx={{ color: 'text.secondary' }} fontSize="small" />}
    </Stack>
  )
}

export const AssociationRow = memo(AssociationRowRaw)
